import { createMemo, createUniqueId, For, Match, Show, Switch } from "solid-js";
import { useI18n } from "@/api/i18n";
import {
  COLOR_TRANSITION,
  FOCUS_RING,
} from "@/domains/freelance/taxonomy/components/sector-selector/primitives";
import {
  categoryLabel,
  subcategoriesOf,
  subcategoryLabel,
  taxonomyStatus,
} from "@/domains/freelance/taxonomy/store/taxonomy-store";
import type { JobCategory } from "@/domains/job-offer/create/constants";
import { cn } from "@/libs/cn";
import {
  chooseWholeCategory,
  isFull,
  type OfferSubcategories,
  toggleOfferSubcategory,
  visibleSubcategories,
} from "../utils/offer-subcategories";

const I18N = "jobOffers.refine.subcategories";

const ROW_CLASS = cn(
  "flex min-h-8 w-full items-center gap-2 rounded-md px-2 py-1 text-left text-sm text-foreground hover:bg-accent",
  COLOR_TRANSITION,
  FOCUS_RING,
);

const NOTE_CLASS = "px-2 py-1 text-xs text-foreground/70";

/** The box of a row: square, because more than one item can be picked here. */
const Tick = (props: { on: boolean }) => (
  <span
    class={cn(
      "flex size-4 shrink-0 items-center justify-center rounded-[4px] border text-[10px]",
      COLOR_TRANSITION,
      props.on ? "border-ring bg-ring text-white" : "border-gray-500 bg-white",
    )}
    aria-hidden="true"
  >
    <Show when={props.on}>
      <i class="ph ph-check font-bold" />
    </Show>
  </span>
);

export type SubcategoryChoiceFieldProps = {
  category: JobCategory | null;
  value: OfferSubcategories;
  onChange: (value: OfferSubcategories) => void;
};

/**
 * The «Ambiti» field of the «Titolo e categoria» dialog: «Tutta la categoria»
 * (a radio) and up to 3 subcategories of the category (toggle buttons). It
 * always works on the visible value, never on the raw one: keys the tree drops
 * are not shown and do not count toward the cap. Without a category, or without
 * the tree, it offers no choice.
 */
export const SubcategoryChoiceField = (props: SubcategoryChoiceFieldProps) => {
  const { t } = useI18n();
  const labelId = createUniqueId();
  const limitId = createUniqueId();

  const visible = createMemo(() =>
    visibleSubcategories(props.category, props.value),
  );
  const full = () => isFull(visible());
  const whole = () => visible().kind === "whole";
  const isPicked = (key: string) => {
    const value = visible();
    return value.kind === "chosen" && value.keys.includes(key);
  };
  const chosenKeys = () => {
    const value = visible();
    return value.kind === "chosen" ? value.keys : [];
  };

  const toggle = (key: string) => {
    if (full() && !isPicked(key)) return;
    props.onChange(toggleOfferSubcategory(props.category, visible(), key));
  };

  return (
    <fieldset aria-labelledby={labelId} class="flex min-w-0 flex-col gap-0.5">
      <p id={labelId} class="text-sm font-medium text-foreground">
        {t(`${I18N}.label`)}
      </p>
      <Switch>
        <Match when={props.category === null}>
          <p class={NOTE_CLASS}>{t(`${I18N}.chooseCategoryFirst`)}</p>
        </Match>
        <Match when={taxonomyStatus() !== "ready"}>
          <p class={NOTE_CLASS}>{t(`${I18N}.taxonomyUnavailable`)}</p>
          <Show when={chosenKeys().length > 0}>
            <ul class="flex flex-wrap gap-1 px-2">
              <For each={chosenKeys()}>
                {(key) => (
                  <li class="rounded-md bg-accent px-2 py-0.5 text-xs text-foreground">
                    {subcategoryLabel(key)}
                  </li>
                )}
              </For>
            </ul>
          </Show>
        </Match>
        <Match when={props.category}>
          {(category) => (
            <>
              <div
                role="radiogroup"
                aria-label={t(`${I18N}.groupLabel`, {
                  category: categoryLabel(category()),
                })}
              >
                {/* biome-ignore lint/a11y/useSemanticElements: same row as the subcategory buttons: a native radio would need its own label and styling */}
                <button
                  type="button"
                  role="radio"
                  class={ROW_CLASS}
                  aria-checked={whole()}
                  onClick={() => props.onChange(chooseWholeCategory())}
                >
                  <Tick on={whole()} />
                  {t(`${I18N}.wholeCategory`)}
                </button>
              </div>
              <ul class="flex flex-col">
                <For each={subcategoriesOf(category())}>
                  {(key) => (
                    <li>
                      <button
                        type="button"
                        class={ROW_CLASS}
                        aria-pressed={isPicked(key)}
                        aria-disabled={
                          full() && !isPicked(key) ? "true" : undefined
                        }
                        aria-describedby={
                          full() && !isPicked(key) ? limitId : undefined
                        }
                        onClick={() => toggle(key)}
                      >
                        <Tick on={isPicked(key)} />
                        <span class="min-w-0">{subcategoryLabel(key)}</span>
                      </button>
                    </li>
                  )}
                </For>
              </ul>
              <Show when={visible().kind === "undeclared"}>
                <p class={NOTE_CLASS}>{t(`${I18N}.undeclaredField`)}</p>
              </Show>
              <Show when={full()}>
                <p id={limitId} class={NOTE_CLASS}>
                  {t(`${I18N}.limit`)}
                </p>
              </Show>
            </>
          )}
        </Match>
      </Switch>
    </fieldset>
  );
};
